// Plain-language Manufacturing guide. Each section is a list of blocks:
//   p        — a paragraph
//   steps    — numbered steps
//   list     — bullet points
//   example  — a worked example (title + lines)
//   tip      — a helpful note
//   warn     — something to be careful about
//   faq      — question + answer pairs
// `actions` are buttons that take the reader straight to the right screen:
//   { label, action }  where action is 'newBom' | 'newOrder' | 'tab:<key>' | 'url:<path>'

export default [
  {
    id: 'overview',
    title: 'What this module does',
    icon: 'bi-compass',
    keywords: 'start introduction overview what is manufacturing production',
    blocks: [
      { type: 'p', text: 'Manufacturing helps you turn raw materials into products you sell — and keeps your stock, costs and records correct while you do it.' },
      { type: 'p', text: 'It works for many kinds of businesses: animal feed, bakeries, soap and detergents, cosmetics, building blocks, juices, furniture and more.' },
      { type: 'list', title: 'The journey in seven steps', items: [
        'Buy your raw materials (flour, maize, oil, cement…) and record them as a purchase.',
        'Check the quality of what arrived, if you need to (optional).',
        'Write down your recipe once — what goes into one batch.',
        'Plan a production run: “make 500 loaves today”.',
        'Make it, then record what you really used and how much you really got.',
        'Sell the finished product at the counter or on an invoice.',
        'Look back any time: what did a batch cost, who bought it, where did its materials come from.',
      ] },
      { type: 'tip', text: 'You don’t have to use every feature. A small bakery can skip lots and quality checks entirely; a feed mill will want all of them.' },
    ],
    actions: [{ label: 'Create my first recipe', action: 'newBom' }],
  },
  {
    id: 'sample',
    title: 'Try it with sample data',
    icon: 'bi-play-circle',
    keywords: 'sample demo example try test seed data practice feed mill kericho end to end',
    blocks: [
      { type: 'p', text: 'Want to see everything working before using your own products? Load a complete sample into this business with one click.' },
      { type: 'list', title: 'What the sample does, start to finish', items: [
        'Buys maize germ, wheat bran, sunflower cake, limestone, salt, premix and empty bags from a grain trader in Eldoret.',
        'Tests each delivery (moisture, aflatoxin) and releases it.',
        'Writes a dairy-meal recipe, which a second person approves.',
        'Makes a batch: plans 30 bags, really gets 29, and works out the cost per bag.',
        'Sells 10 bags for cash at the mill gate.',
        'Invoices 15 bags to an agrovet in Kericho, sends the truck, records who received it, and records the M-Pesa payment.',
        'Pays the supplier by bank — so at the end nobody owes anybody and the stock matches the books.',
      ] },
      { type: 'tip', text: 'Everything it adds is labelled “(sample)” so you can tell it apart. Your existing products and sales are not changed. The second approver is another user in your business (one is created if you work alone).' },
      { type: 'warn', text: 'Sample data is only available on test systems, and only once per business.' },
    ],
    actions: [{ label: 'Load sample data', action: 'sampleData' }],
  },
  {
    id: 'setup',
    title: 'Before you start',
    icon: 'bi-list-check',
    keywords: 'setup prepare products raw materials finished goods cost price buying price',
    blocks: [
      { type: 'p', text: 'Manufacturing uses the same products you already have in Inventory. You need two kinds:' },
      { type: 'list', items: [
        'Raw materials — what you use up (flour, sugar, maize germ, empty bags, bottles, labels).',
        'Finished products — what you make and sell (white bread, dairy meal 70 kg bag, 1 litre liquid soap).',
      ] },
      { type: 'steps', title: 'Checklist', items: [
        'Add each raw material and finished product in Inventory if it isn’t there yet.',
        'Give every raw material a buying price (cost). Production cannot be costed without it — and the selling price is never used as a cost.',
        'Decide the unit you count each product in (kg, litres, pieces, bags). See “Units of measure”.',
        'Nothing else to set up for recipes: when you create a recipe you just pick the product it makes. (Behind the scenes it is linked to a Grouped item, which you can see in the Grouped items tab.)',
        'Optional: switch on lot tracking and quality holds for products that need them (Settings tab).',
      ] },
      { type: 'warn', text: 'Grouped items you already sell at the counter keep working exactly as before. A group only changes once it has an approved recipe here.' },
    ],
    actions: [
      { label: 'Open Inventory', action: 'url:/inventory' },
      { label: 'Open Grouped items', action: 'tab:grouped' },
      { label: 'Manufacturing settings', action: 'tab:settings' },
    ],
  },
  {
    id: 'units',
    title: 'Units of measure (kg, bags, litres…)',
    icon: 'bi-rulers',
    keywords: 'unit units kg tonne gram litre bag bags pack conversion convert measure',
    blocks: [
      { type: 'p', text: 'Every product is stocked in one unit — its “stock unit”. You can still buy, plan and sell in other units; the system converts for you.' },
      { type: 'list', title: 'Converted automatically', items: [
        'Weight: grams, kilograms and tonnes (1 tonne = 1,000 kg).',
        'Volume: millilitres and litres.',
      ] },
      { type: 'p', text: 'Anything else needs a rule you add once, on the product (Settings tab → Product units). For example “1 bag = 50 kg” or “1 crate = 24 bottles”.' },
      { type: 'example', title: 'Example — feed sold in bags', lines: [
        'Maize is stocked in kg. You buy 1 tonne → stock goes up by 1,000 kg.',
        'Dairy meal is stocked in bags, with the rule 1 kg = 1/70 bag.',
        'You produce 2,100 kg of meal → stock goes up by 30 bags.',
      ] },
      { type: 'example', title: 'Example — bakery', lines: [
        'Flour stocked in kg; recipe uses 500 g per batch → the system takes 0.5 kg.',
        'Bread stocked in pieces; a “pack of 6” unit lets you sell packs.',
      ] },
      { type: 'warn', text: 'Kilograms are never turned into litres unless you add a rule for that product (for example molasses: 1 litre = 1.4 kg). This prevents silent mistakes.' },
      { type: 'tip', text: 'A product’s stock unit can’t be changed once it has stock — add an extra unit instead.' },
    ],
    actions: [{ label: 'Add a product unit', action: 'tab:settings' }],
  },
  {
    id: 'receive',
    title: 'Receiving raw materials',
    icon: 'bi-box-arrow-in-down',
    keywords: 'purchase buy receive supplier delivery stock in restock lot supplier lot expiry',
    blocks: [
      { type: 'p', text: 'Record deliveries on the Purchases page as you do today. Quantities can be decimals (12.5 kg) and in any unit the product supports (1 tonne).' },
      { type: 'p', text: 'For products with lot tracking, also fill in the supplier’s lot/batch number and the expiry date — this is what makes recalls possible later.' },
      { type: 'example', title: 'Example', lines: [
        'Supplier: Grain Traders Ltd. Maize germ, 5 tonnes at KES 38,000 per tonne, supplier lot KSM-114, expires March.',
        'Stock goes up by 5,000 kg at KES 38 per kg, in a new lot with that supplier lot number.',
      ] },
      { type: 'tip', text: 'If the product needs a quality check, the new lot waits in “Quarantined” until someone releases it. It cannot be used or sold before then.' },
      { type: 'steps', title: 'Buying against a requisition', items: [
        'The store raises a requisition (Inventory → Requisitions) for what production needs.',
        'Once it is approved, open it and click “Record purchase (receive stock)”. Bulk Restock opens with the outstanding quantities already filled in.',
        'Change the quantities to what actually arrived and save. The requisition shows Received / Outstanding and moves to “Partly received”, then “Received”.',
      ] },
      { type: 'p', text: 'New buying price: tick “apply this new buying price immediately” to change the cost now. Leave it unticked and the cost switches by itself once the old stock is used up — the switch shows in Inventory Activity as “Auto buying price update”.' },
    ],
    actions: [{ label: 'Go to Purchases', action: 'url:/purchases' }, { label: 'Requisitions', action: 'url:/requisitions' }, { label: 'See lots', action: 'tab:lots' }],
  },
  {
    id: 'quality',
    title: 'Quality checks and releasing lots',
    icon: 'bi-clipboard-check',
    keywords: 'quality qc test check release reject quarantine moisture aflatoxin ph inspection hold',
    blocks: [
      { type: 'p', text: 'Quality checks are optional. Turn them on only for products that need testing before use or sale.' },
      { type: 'steps', title: 'How it works', items: [
        'Settings tab → add your tests once, with limits. Example: Moisture, max 13.5 %.',
        'Product units & lot tracking → switch on “Hold new lots for quality release” for that product.',
        'When a delivery or a finished batch arrives, open it in Lots & QC and record the results.',
        'Results outside the limits are marked FAIL automatically.',
        'Release the lot when every test passes, or reject it with a reason.',
      ] },
      { type: 'example', title: 'Examples by industry', lines: [
        'Feed: moisture and aflatoxin on maize.',
        'Soap / cosmetics: pH and viscosity on each batch.',
        'Building blocks: use the hold as “curing” — release the batch when it has cured.',
      ] },
      { type: 'warn', text: 'Quarantined or rejected lots cannot be used in production, sold at the counter or dispatched.' },
    ],
    actions: [{ label: 'Open Lots & QC', action: 'tab:lots' }, { label: 'Set up quality tests', action: 'tab:settings' }],
  },
  {
    id: 'recipes',
    title: 'Recipes (bill of materials)',
    icon: 'bi-journal-text',
    keywords: 'recipe bom bill of materials ingredients formula create stages packaging yield version approve',
    blocks: [
      { type: 'p', text: 'A recipe says what goes into one standard batch. You write it once and the system scales it for any quantity.' },
      { type: 'steps', title: 'Create a recipe', items: [
        'Click New BOM and search for the product the recipe makes (e.g. Chapati). If it isn’t in your inventory yet, add it first.',
        'Set the batch size and unit — for example 1,000 kg, or 100 loaves.',
        'Add stages if you like (Mixing, Baking, Packing) and the ingredients for each, with quantity and unit.',
        'Add packaging as ingredients too (bags, bottles, labels).',
        'Optional: expected yield (98 % means you lose 2 % on average) and conversion cost (labour, power) per unit.',
        'Save — it is a draft. Submit it for approval; a second person approves it.',
      ] },
      { type: 'steps', title: 'Change a recipe', items: [
        'Open the recipe and click “Edit (new version)”. The form opens with everything filled in — ingredients, stages, yield, conversion cost and substitutes.',
        'Make your changes and save. They are saved as the next version (e.g. v3) as a draft; the version you edited is not changed.',
        'Submit the new version for approval. Until it is approved, production keeps using the current approved version.',
      ] },
      { type: 'example', title: 'Example — bread (batch of 100 loaves)', lines: [
        'Flour 50 kg · Water 30 l · Yeast 1 kg · Salt 1 kg · Bread bags 100 pcs.',
        'Expected yield 96 % (a few loaves come out misshapen).',
        'To make 300 loaves the system plans 156.25 kg of flour, and so on.',
      ] },
      { type: 'example', title: 'Example — liquid soap by percentage (ratio recipe)', lines: [
        'Water 80 % · Base 15 % · Salt 3 % · Colour & scent 2 % — must add up to 100 %.',
      ] },
      { type: 'tip', text: 'Changing a recipe creates a new version. Old production orders keep the version they used, so history is never rewritten.' },
      { type: 'tip', text: 'Selling the batch in several bag sizes (50 kg, 20 kg, 10 kg)? Add Pack sizes on the recipe — see “Packing one batch into different bag sizes”.' },
      { type: 'tip', text: 'Using formulation software? Its recipe can be imported as a draft (ask support for the import link).' },
    ],
    actions: [{ label: 'Create a recipe', action: 'newBom' }, { label: 'See all recipes', action: 'tab:boms' }],
  },
  {
    id: 'orders',
    title: 'Production orders — making a batch',
    icon: 'bi-gear',
    keywords: 'production order batch make plan approve start complete stage run job',
    blocks: [
      { type: 'steps', title: 'From plan to finished stock', items: [
        'New Order → pick the product, how much to make and the planned start date and time. You see every ingredient needed and whether there is enough free stock.',
        'Submit for approval. A second person approves; the system checks stock again so two orders can’t count the same materials.',
        'Start, and tick off each stage as it is done.',
        'Record any spill or reject as wastage while you work.',
        'Complete: enter what you REALLY used of every ingredient and how much good product you got. If the recipe has pack sizes, also enter how many bags of each size you filled.',
        'Stock is updated in one go: materials out, finished product in, costs worked out.',
      ] },
      { type: 'example', title: 'Example', lines: [
        'Plan: 2,100 kg dairy meal (30 bags).',
        'Reality: used 5 kg more maize than planned, one bag tore.',
        'Enter the real maize figure and 2,030 kg good output → 29 bags in stock.',
      ] },
      { type: 'list', title: 'Other actions', items: [
        'Made less than planned? Tick “create remainder order” when completing — a new order is opened for the rest.',
        'Cancel: before completion nothing has moved, so cancelling is safe.',
        'Reverse: undoes a completed order posted by mistake — only if none of the batch has been used or sold yet.',
        'Print a production sheet for the floor and a label for the batch.',
      ] },
    ],
    actions: [{ label: 'Start a production order', action: 'newOrder' }, { label: 'See orders', action: 'tab:orders' }],
  },
  {
    id: 'packing',
    title: 'Packing one batch into different bag sizes',
    icon: 'bi-bag',
    keywords: 'pack pack-out packing bags 50kg 20kg 10kg 70kg sizes package packaging sack bale bottle sizes',
    blocks: [
      { type: 'p', text: 'Mix once, pack into several sizes: for example 500 kg of dairy meal into 50 kg, 20 kg and 10 kg bags in the same production order.' },
      { type: 'steps', title: 'Set up once', items: [
        'Create one product per bag size, counted per bag: “Dairy Meal 50kg”, “Dairy Meal 20kg”, “Dairy Meal 10kg”, each with its own selling price.',
        'Optional: products for the empty bags (“Empty bag 50kg” …). Buy them as usual so their cost goes into each packed bag.',
        'Open the recipe → Pack sizes → add each size: the packed product, how much it holds (50 kg) and the empty bag it uses.',
      ] },
      { type: 'steps', title: 'Each production run', items: [
        'Run the order as usual. At Complete, enter the good output (e.g. 500 kg).',
        'In Pack-out, type how many bags of each size you filled. The running total shows how much is packed and what is left.',
        'Anything not packed: keep it as loose bulk in stock, or write it off as waste.',
      ] },
      { type: 'example', title: 'Example — 500 kg of dairy meal', lines: [
        '6 × 50 kg = 300 kg · 7 × 20 kg = 140 kg · 6 × 10 kg = 60 kg → 500 kg packed, nothing left over.',
        'Stock: +6 Dairy Meal 50kg, +7 Dairy Meal 20kg, +6 Dairy Meal 10kg; the empty bags are used up.',
        'Cost per bag = its share of the batch cost by weight + the empty bag. A 50 kg bag carries five times the feed cost of a 10 kg bag.',
      ] },
      { type: 'tip', text: 'All bags from one batch share the batch number, so a recall finds every size. You cannot pack more than you produced.' },
    ],
    actions: [{ label: 'See all recipes', action: 'tab:boms' }, { label: 'Start a production order', action: 'newOrder' }],
  },
  {
    id: 'wastage',
    title: 'Wastage and losses',
    icon: 'bi-trash',
    keywords: 'waste wastage loss spill reject scrap broken torn yield normal abnormal',
    blocks: [
      { type: 'p', text: 'Record losses as they happen so you know where money goes. There are two kinds:' },
      { type: 'list', items: [
        'Ingredient wastage — some flour spilt, some timber cut off.',
        'Rejected output — burnt loaves, torn bags, cracked blocks.',
      ] },
      { type: 'warn', text: 'Wastage is counted ONCE. When you complete the order, the “actual used” figure must include anything spilt. The system never deducts the spill a second time.' },
      { type: 'p', text: 'Each stage has an expected loss. Losses well above it are marked “abnormal” — they are reported separately and not hidden in the product cost.' },
      { type: 'tip', text: 'Losses found later in the store (rats, damp) are not production wastage — record them as a stock adjustment.' },
    ],
    actions: [{ label: 'See orders in progress', action: 'tab:orders' }],
  },
  {
    id: 'costs',
    title: 'How costs are worked out',
    icon: 'bi-calculator',
    keywords: 'cost costing price unit cost margin profit weighted average last cost overhead labour',
    blocks: [
      { type: 'p', text: 'The cost of a batch = materials really used (at their cost price on the day) + labour and power cost from the recipe − any abnormal loss.' },
      { type: 'example', title: 'Worked example', lines: [
        'Maize germ 600 kg × KES 30 = 18,000',
        'Wheat bran 400 kg × KES 20 = 8,000',
        'Labour & power: 1,000 kg × KES 2 = 2,000',
        'Total 28,000 for 1,000 kg → KES 28 per kg.',
        'That KES 28 becomes the cost price of the finished product, so your sales profit is correct.',
      ] },
      { type: 'list', title: 'Costing method (Settings)', items: [
        'Last cost — the newest buying price is used (today’s default).',
        'Weighted average — new purchases are blended with what you already have. Better when prices change often. Ask your accountant which to use.',
      ] },
    ],
    actions: [{ label: 'Costing settings', action: 'tab:settings' }, { label: 'Stock valuation report', action: 'tab:reports' }],
  },
  {
    id: 'selling',
    title: 'Selling finished products',
    icon: 'bi-bag-check',
    keywords: 'sell sale pos counter invoice dealer delivery note dispatch return customer credit',
    blocks: [
      { type: 'list', items: [
        'At the counter (POS): sell the finished product like any other item. Products with lots are taken from released lots, earliest expiry first.',
        'On credit to dealers: create the invoice as usual, then click “Create delivery note” on the invoice (or Sales → Deliveries → “Dispatch an invoice”). The delivery note takes the stock out, picks the batches and prints for the driver. You can deliver part now and “Dispatch the rest” later.',
        'Proof of delivery: tick the SMS box when dispatching. The client gets a link (with the invoice and a 6-digit delivery code) and the driver gets theirs — no Dukakit account needed. The client signs on their phone or the driver’s, or gives the driver the code. You can also sign on your own screen with “Sign / mark delivered”.',
        'Click any delivery note to see everything: items still to deliver, the invoice balance, the signature and where it was signed.',
        'Returns: on a delivery note click Return — the goods go back into the same batch they left from.',
      ] },
      { type: 'tip', text: 'Attach the customer to counter sales whenever you can. It is what lets you call them in a recall.' },
    ],
    actions: [{ label: 'Open the counter (POS)', action: 'url:/makesale' }, { label: 'Deliveries', action: 'url:/deliveries' }],
  },
  {
    id: 'trace',
    title: 'Tracing a batch (recalls)',
    icon: 'bi-diagram-3',
    keywords: 'trace traceability recall batch lot supplier customer where did it go came from',
    blocks: [
      { type: 'p', text: 'Search any supplier lot, batch number or production order number in the Traceability tab.' },
      { type: 'list', items: [
        'Forward: a bad delivery → every batch that used it → every customer who bought those batches.',
        'Backward: a complaint about a batch → every ingredient lot in it → the supplier of each.',
      ] },
      { type: 'example', title: 'Example', lines: [
        'The supplier warns that maize lot KSM-114 had high aflatoxin.',
        'Search KSM-114 → it was used in batch PRD-202610-0003 → sold to Kiambu Agrovet and two walk-in farmers.',
      ] },
    ],
    actions: [{ label: 'Open Traceability', action: 'tab:trace' }],
  },
  {
    id: 'reports',
    title: 'Reports and checking your numbers',
    icon: 'bi-graph-up',
    keywords: 'report reports reconciliation valuation margin yield variance accounting gl ledger balance',
    blocks: [
      { type: 'list', items: [
        'Production & yield: how much you planned vs made, material usage, wastage, cost per unit.',
        'Stock valuation: what your stock is worth today.',
        'Margins: profit on each finished product.',
        'Reconciliation: confirms stock on hand matches the stock records and the accounts. Green means everything adds up.',
      ] },
      { type: 'tip', text: 'Look at Reconciliation once a week. If something is out, it tells you which product.' },
    ],
    actions: [{ label: 'Open Reports', action: 'tab:reports' }],
  },
  {
    id: 'roles',
    title: 'Who does what (approvals)',
    icon: 'bi-people',
    keywords: 'permission role approve approval segregation duties second person staff owner supervisor',
    blocks: [
      { type: 'p', text: 'By default, recipes and production orders must be approved by someone other than the person who created them — even an Admin. This stops mistakes and fraud.' },
      { type: 'p', text: 'Small team with nobody else to approve? An Admin or owner can approve their own recipe or order by giving a reason (at least 10 characters). The reason is saved and shown on the recipe and the order history.' },
      { type: 'p', text: 'One-person business? Switch off “Segregation of duties” in the Settings tab.' },
      { type: 'list', title: 'Typical split', items: [
        'Storekeeper: receives materials, dispatches deliveries.',
        'Quality: records test results, releases or rejects lots.',
        'Supervisor: approves recipes and orders, completes production.',
        'Owner / accountant: reports and reconciliation.',
      ] },
    ],
    actions: [{ label: 'Manufacturing settings', action: 'tab:settings' }],
  },
  {
    id: 'faq',
    title: 'Problems and how to fix them',
    icon: 'bi-question-circle',
    keywords: 'error problem help fix cannot why not enough stock approve cost unit convert faq troubleshooting',
    blocks: [
      { type: 'faq', items: [
        { q: '“Cannot approve — not enough free stock”', a: 'Another approved order already reserved those materials, or the lots are still quarantined. Receive more stock, release lots, or cancel the other order.' },
        { q: '“No buying price recorded for …”', a: 'Give that raw material a cost price in Inventory or record a purchase. The selling price is never used as a cost.' },
        { q: '“Cannot convert ‘bag’ to ‘kg’”', a: 'Add a unit rule for that product in Settings → Product units (for example 1 bag = 50 kg).' },
        { q: '“Only an approved recipe can be used”', a: 'Open the recipe, Submit for approval, and ask a second person to approve it.' },
        { q: '“Enter the actual quantity used for …”', a: 'When completing, every ingredient needs its real figure — enter 0 if none was used.' },
        { q: 'The batch can’t be sold at the counter', a: 'It is waiting for quality release. Release it from the order (Quality release) or from Lots & QC.' },
        { q: 'I completed an order by mistake', a: 'Use Reverse on the order, as long as none of the batch has been sold or used yet.' },
        { q: 'I edited a recipe but production still uses the old one', a: 'The edit is a new draft version. Submit it for approval; once approved it replaces the old version for new orders.' },
        { q: '“The packs hold more than was produced”', a: 'Lower the number of bags in Pack-out, or correct the good output figure.' },
        { q: 'The client did not get the delivery SMS', a: 'Open the delivery note → Links & SMS, check the phone number and send again. A new delivery code is sent; the old one stops working.' },
        { q: '“Too many wrong codes”', a: 'After 5 wrong codes the driver link locks. Resend the SMS to the client from Links & SMS to issue a new code.' },
        { q: 'The buying price did not change after a restock', a: 'If “apply immediately” was not ticked, it changes once the old stock is used up. A manual price edit after the restock keeps your price.' },
      ] },
    ],
  },
  {
    id: 'glossary',
    title: 'Words used in this module',
    icon: 'bi-book',
    keywords: 'glossary words meaning definition terms',
    blocks: [
      { type: 'faq', items: [
        { q: 'Recipe / BOM', a: 'The list of ingredients and quantities for one batch (“bill of materials”).' },
        { q: 'Batch', a: 'One production run and the finished goods it made.' },
        { q: 'Lot', a: 'A group of stock that arrived or was made together — a supplier delivery or a production batch.' },
        { q: 'Quarantined', a: 'Waiting for quality checks; cannot be used or sold.' },
        { q: 'Yield', a: 'How much good product you get from what you put in, e.g. 98 %.' },
        { q: 'Wastage', a: 'Material or product lost during production.' },
        { q: 'Earliest expiry first', a: 'The system uses or sells the lot that expires soonest before newer ones.' },
        { q: 'Reconciliation', a: 'A check that your stock records, physical stock and accounts agree.' },
        { q: 'Pack-out', a: 'Filling a finished batch into bags or containers of different sizes at completion.' },
        { q: 'Delivery note', a: 'The document that sends invoiced goods out of stock to the customer, with proof of delivery.' },
        { q: 'Requisition', a: 'An internal request to buy stock; purchases can be received against it.' },
      ] },
    ],
  },
];
